import styled from '@emotion/styled';
import { formatNumberGroupingSeparators } from '@vmos2/core';
import { Button, CircularProgressBar, Title, Text, Link } from '@vmos2/design-system';
import { LoyaltyLogo, StampCard } from '@vmos2/loyalty';
import { useDeviceSize, useSettings } from '@vmos2/providers';
import React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  loyaltyData: { points?: number },
  numberUntilReward: number,
  progressPercentage: number,
  routeParams: { fromRoute: string, fromParams: string },
  withProgressBar: boolean,
};

export const RewardsPopupContent = ({
  loyaltyData,
  numberUntilReward,
  progressPercentage,
  routeParams,
  withProgressBar,
}: Props) => {
  const { isMobile } = useDeviceSize();
  const { loyaltyStampCard } = useSettings();
  const { t } = useTranslation('loyalty');
  const userRewardPoints = loyaltyData?.points || 0;

  return (
    <>
      <DropdownContent data-test="dropdown-content">
        {loyaltyStampCard?.enabled ? (
          <StampCard height={isMobile ? '84px' : '120px'} mx="auto" userPoints={userRewardPoints} />
        ) : (
          <CircularProgressBarWrapper data-test="circular-progress-bar-wrapper" withProgressBar={withProgressBar}>
            <CircularProgressBar
              logo={<LoyaltyLogo />}
              percentage={progressPercentage}
              size={withProgressBar ? 96 : 40}
              withProgressBar={withProgressBar}
            />
          </CircularProgressBarWrapper>
        )}

        <Title dataTest="loyalty-balance-title" level={2} mb={0} mt={4}>
          <span data-test="loyalty-balance">{formatNumberGroupingSeparators(userRewardPoints)} </span>

          {t('common:point', {count: userRewardPoints})}
        </Title>

        {!!numberUntilReward && (
          <Text dataTest="loyalty-next-reward" display="block" level={4} mt={2}>
            {t('pointsUntilReward', {
              count: numberUntilReward,
              points: formatNumberGroupingSeparators(numberUntilReward)
            })}
          </Text>
        )}
      </DropdownContent>

      <DropdownFooter>
        <Button
          as={Link}
          block
          contentCenter
          params={routeParams}
          size="small"
          to="profile.rewards"
          type="primary"
        >
          {t('goToRewardsPage')}
        </Button>
      </DropdownFooter>
    </>
  );
};

const CircularProgressBarWrapper = styled('div')(({ theme, withProgressBar }) => `
  display: inline-block;
  ${!withProgressBar ? `margin: ${theme.margins.xl};` : ''}
`);

const DropdownContent = styled('div')`
  width: 100%;
  padding: ${({ theme }) => theme.margins.xl};
  overflow: auto;
  color: ${({ theme }) => theme.color.black};
`;

const DropdownFooter = styled('div')`
  position: sticky;
  bottom: 0;
  width: 100%;
  padding: ${({ theme }) => theme.margins.md};
  box-shadow: 0 0 12px hsl(212 4.8% 44.9% / 30%);
  background: inherit;
`;
